/**
 * Search History Service
 * Handles API request log queries for search history display
 */

import { makeAuthenticatedRequest, createApiError } from './apiService';
import { API_ENDPOINTS } from '../config/api';

export interface SearchHistoryEntry {
  id: string;
  vin: string;
  searchedAt: string;
  cost: number;
  wasCached: boolean;
  username: string;
  email: string;
  status: string;
  requestType: string;
  state?: string | null;
}

export interface SearchHistoryResponse {
  searches: SearchHistoryEntry[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface ApiRequestLogDetail {
  id: string;
  vin: string;
  requestType: string;
  searchedAt: string;
  status: string;
  responseBody: string | null;
}

export interface MvrLogDetail extends ApiRequestLogDetail {
  stateCode: string | null;
  recordType: 'title' | 'registration' | 'both' | null;
  reportId: string | null;
}

/** Reports drill-down filters; they narrow the caller's scope, never widen it. */
export interface SearchHistoryDrill {
  filterUserId?: string;
  integrationOnly?: boolean;
  state?: string;
}

export interface DashboardStats {
  pdfReportsThisMonth: number;
  pdfReportsLastMonth: number;
  searchesThisMonth: number;
  searchesLastMonth: number;
}

/**
 * Fetch search history from API request logs
 * Shows all individual VIN searches including duplicates
 * @param pageNumber - Page number (default: 1)
 * @param pageSize - Items per page (default: 100)
 * @param vin - Optional VIN filter
 * @param requestType - Optional request type filter (NMVTIS, VIN Decode, NVS, MVR)
 * @param startDate - Optional start date filter (ISO date string)
 * @param endDate - Optional end date filter (ISO date string)
 * @param orgWide - Optional organization-wide scope
 * @param drill - Optional Reports drill-down filters (user / integration / state)
 * @returns Paginated search history response
 */
export const getSearchHistory = async (
  pageNumber: number = 1,
  pageSize: number = 100,
  vin?: string,
  requestType?: string,
  startDate?: string,
  endDate?: string,
  orgWide?: boolean,
  drill?: SearchHistoryDrill
): Promise<SearchHistoryResponse> => {
  try {
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
    });

    if (vin) {
      params.append('vin', vin);
    }

    if (requestType) {
      params.append('requestType', requestType);
    }

    if (startDate) {
      params.append('startDate', startDate);
    }

    if (endDate) {
      params.append('endDate', endDate);
    }

    if (orgWide) {
      params.append('orgWide', 'true');
    }

    if (drill?.filterUserId) {
      params.append('filterUserId', drill.filterUserId);
    }

    if (drill?.integrationOnly) {
      params.append('integrationOnly', 'true');
    }

    if (drill?.state) {
      params.append('state', drill.state);
    }

    const url = `${API_ENDPOINTS.API_LOGS.SEARCH_HISTORY}?${params.toString()}`;
    const response = await makeAuthenticatedRequest(url, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await createApiError(response);
      throw error;
    }

    const data: SearchHistoryResponse = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Export search history as CSV file
 * @param vin - Optional VIN filter
 * @param requestType - Optional request type filter
 * @param startDate - Optional start date filter (ISO date string)
 * @param endDate - Optional end date filter (ISO date string)
 */
export const exportSearchHistoryCsv = async (
  vin?: string,
  requestType?: string,
  startDate?: string,
  endDate?: string,
  orgWide?: boolean
): Promise<void> => {
  try {
    const params = new URLSearchParams();

    if (vin) {
      params.append('vin', vin);
    }

    if (requestType) {
      params.append('requestType', requestType);
    }

    if (startDate) {
      params.append('startDate', startDate);
    }

    if (endDate) {
      params.append('endDate', endDate);
    }

    if (orgWide) {
      params.append('orgWide', 'true');
    }

    const queryString = params.toString();
    const url = queryString
      ? `${API_ENDPOINTS.API_LOGS.EXPORT_CSV}?${queryString}`
      : API_ENDPOINTS.API_LOGS.EXPORT_CSV;

    const response = await makeAuthenticatedRequest(url, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await createApiError(response);
      throw error;
    }

    // Get the blob and trigger download
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;

    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `ActivityLog_${new Date().toISOString().split('T')[0]}.csv`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    throw error;
  }
};

/**
 * Fetch a specific API request log by ID with full response body
 * Used to display historical report data without making new API calls
 * @param logId - The unique identifier of the log entry
 * @returns Full log entry details including response body
 */
export const getLogById = async (logId: string): Promise<ApiRequestLogDetail> => {
  try {
    const url = API_ENDPOINTS.API_LOGS.GET_BY_ID(logId);
    const response = await makeAuthenticatedRequest(url, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await createApiError(response);
      throw error;
    }

    const data: ApiRequestLogDetail = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetch dashboard statistics including PDF generation counts and search counts
 * @returns Dashboard statistics for current and previous month
 */
export const getDashboardStats = async (): Promise<DashboardStats> => {
  try {
    const url = API_ENDPOINTS.API_LOGS.DASHBOARD_STATS;
    const response = await makeAuthenticatedRequest(url, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await createApiError(response);
      throw error;
    }

    const data: DashboardStats = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * State code → state name lookup used when building MVR PDF endpoint URLs.
 * Mirrors the server-side mapping in StateDMVController.GetStateName so that
 * mvr-pdf URLs match the singleton route format.
 */
const STATE_CODE_TO_NAME: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};

/**
 * Fetches the MVR PDF for an activity log entry as a Blob.
 * Resolves state and record type from the log row, then routes to the
 * idahop2p PDF endpoint for Idaho or the state-dmv mvr-pdf endpoint for everything else.
 */
export const fetchMvrPdfBlobFromLog = async (logId: string): Promise<{ blob: Blob; vin: string; stateCode: string }> => {
  const detail = await getMvrLogById(logId);
  const stateCode = (detail.stateCode || '').toUpperCase();
  if (!stateCode) {
    throw new Error('No state code on this MVR log entry');
  }
  const recordType = detail.recordType ?? 'both';
  const vin = detail.vin;

  let response: Response;
  if (stateCode === 'ID') {
    // Idaho's PDF endpoint takes report IDs in the body. The log row carries one
    // ReportId; pass it as both title and registration so the controller picks up
    // whichever side actually has data, regardless of which the bulk handler stored.
    response = await makeAuthenticatedRequest(API_ENDPOINTS.IDAHO_P2P.GENERATE_PDF, {
      method: 'POST',
      body: JSON.stringify({
        VIN: vin,
        StateCode: 'ID',
        TitleReportId: detail.reportId,
        RegistrationReportId: detail.reportId,
      }),
    });
  } else {
    const stateName = STATE_CODE_TO_NAME[stateCode] ?? stateCode;
    const params = new URLSearchParams({ vin, recordType });
    if (detail.reportId) {
      // Pass the report ID on the side the row represents; for "both" we let the
      // server fall back to a VIN+state lookup since one ID can't cover both.
      if (recordType === 'title') {
        params.append('titleReportId', detail.reportId);
      } else if (recordType === 'registration') {
        params.append('registrationReportId', detail.reportId);
      }
    }
    const url = `${API_ENDPOINTS.STATE_DMV.MVR_PDF(stateName)}?${params.toString()}`;
    response = await makeAuthenticatedRequest(url, { method: 'GET' });
  }

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  const blob = await response.blob();
  return { blob, vin, stateCode };
};

/**
 * Fetch MVR-specific log details by ID
 * Includes state code and record type extracted from the endpoint
 * Used to restore previous MVR search results when navigating from activity page
 * @param logId - The unique identifier of the log entry
 * @returns MVR log entry details including state code and record type
 */
export const getMvrLogById = async (logId: string): Promise<MvrLogDetail> => {
  try {
    const url = API_ENDPOINTS.API_LOGS.GET_MVR_BY_ID(logId);
    const response = await makeAuthenticatedRequest(url, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await createApiError(response);
      throw error;
    }

    const data: MvrLogDetail = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};
